import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import { useQuery, useMutation } from '@apollo/react-hooks';
import toast from 'react-hot-toast';
import Card from './Card';
import * as price from '../util/Price';
import Input, { MoneyInput, PercentInput } from './Input';
import {
  GET_CURRENT_ORGANIZATION,
  UPDATE_ORGANIZATION_ROI_SETTINGS,
} from '../graphql/queries';
import Loader from './Loader';
import Button from './Button';
import { UserContext } from '../context/UserContext';
import AccessLevels from '../util/AccessLevels';
import colors from '../styles/colors';

const Form = styled.form`
  width: 60%;

  @media (max-width: 991px) {
    width: 100%;
  }
`;

const LoaderContainer = styled.div`
  position: absolute;
  top: calc(50vh - 60px);
  left: calc(50vw - 60px);
`;

const SliderContainer = styled.div`
  display: flex;
  border: 1px solid #ddd;
  border-radius: 30px;
  overflow: hidden;
  width: fit-content;
  margin: ${(props) => props.margin || '10px 0'};
`;

const SliderButton = styled.button`
  padding: 8px 20px;
  background-color: ${(props) => (props.active ? 'rgb(39, 187, 173)' : 'white')};
  color: ${(props) => (props.active ? 'white' : 'rgb(39, 187, 173)')};
  border: none;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  outline: none;
  transition: background-color 0.3s ease, color 0.3s ease;
  &:hover {
    opacity: 0.9;
  }
`;

const SliderLabel = styled.div`
  font-size: 1.2rem;
  color: ${colors.grey3};
  margin: 30px 0px 10px 0px;
`;

export default function ROI() {
  const { user } = useContext(UserContext);
  const [campaignFollowUpEnabled, setCampaignFollowUpEnabled] = useState(false);
  const [perPersonAverage, setPerPersonAverage] = useState('');
  const [averagePartySize, setAveragePartySize] = useState('');
  const [conversionRate, setConversionRate] = useState('');
  const { data, loading } = useQuery(GET_CURRENT_ORGANIZATION);

  const [updateOrganizationROISettings, { loading: updateLoading }] = useMutation(
    UPDATE_ORGANIZATION_ROI_SETTINGS,
    {
      onCompleted(data) {
        toast.success('ROI settings updated successfully');
      },
      onError(error) {
        console.error(error);
        toast.error('Failed to update ROI settings');
      },
    },
  );

  useEffect(() => {
    if (data && data.getCurrentOrganization) {
      const org = data.getCurrentOrganization;
      setCampaignFollowUpEnabled(org.campaignFollowUpEnabled || false);
      setPerPersonAverage(org.perPersonAverage ? price.output(org.perPersonAverage, true) : '');
      setAveragePartySize(org.averagePartySize ? String(org.averagePartySize) : '');
      setConversionRate(org.conversionRate ? String(org.conversionRate) : '');
    }
  }, [data]);

  if (loading) {
    return (
      <LoaderContainer>
        <Loader />
      </LoaderContainer>
    );
  }

  // Only show to SuperAdmin
  if (user?.currentOrganizationRole !== AccessLevels.SuperAdmin) {
    return null;
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    updateOrganizationROISettings({
      variables: {
        campaignFollowUpEnabled,
        perPersonAverage: perPersonAverage ? Number(price.convertToCents(perPersonAverage)) : null,
        averagePartySize: averagePartySize ? Number(averagePartySize) : null,
        conversionRate: conversionRate ? Number(conversionRate) : null,
      },
    });
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Card width="100%" padding="20px 30px">
        <SliderLabel>ENABLE CAMPAIGN FOLLOW-UP TEXT?</SliderLabel>
        <SliderContainer margin="10px 0 30px 0">
          <SliderButton
            type="button"
            active={!campaignFollowUpEnabled}
            onClick={(e) => {
              e.preventDefault();
              setCampaignFollowUpEnabled(false);
            }}
          >
            No
          </SliderButton>
          <SliderButton
            type="button"
            active={campaignFollowUpEnabled}
            onClick={(e) => {
              e.preventDefault();
              setCampaignFollowUpEnabled(true);
            }}
          >
            Yes
          </SliderButton>
        </SliderContainer>

        <MoneyInput
          top
          width="100%"
          type="text"
          header="PER PERSON AVERAGE"
          value={perPersonAverage}
          onChange={(e) => {
            const amount = e.target.value;
            if (amount.match(/^\d{0,}(\.\d{0,2})?$/)) {
              setPerPersonAverage(amount);
            }
          }}
        />

        <Input
          width="100%"
          type="number"
          header="AVERAGE PARTY SIZE"
          value={averagePartySize}
          onChange={(e) => {
            const value = e.target.value;
            if (value === '' || (Number.isInteger(Number(value)) && Number(value) >= 0)) {
              setAveragePartySize(value);
            }
          }}
          min="0"
        />

        <PercentInput
          width="100%"
          type="number"
          header="CONVERSION RATE"
          value={conversionRate}
          onChange={(e) => {
            const value = e.target.value;
            if (value === '' || (Number.isInteger(Number(value)) && Number(value) >= 0 && Number(value) <= 100)) {
              setConversionRate(value);
            }
          }}
          min="0"
          max="100"
        />

        <Button
          margin="30px 0 0 0"
          text="SAVE"
          type="submit"
          loading={updateLoading}
          width="fit-content"
        />
      </Card>
    </Form>
  );
}

